import type { Task, TaskStatus } from '../api/types'
import { useUpdateTask } from '../api/hooks'
import { describeDueDate } from '../lib/dates'
import { Chip, PriorityFlag, StatusPill, cx } from './ui'

const DONE_TONE = {
  danger: 'text-red-600 dark:text-red-400',
  warn: 'text-amber-600 dark:text-amber-400',
  muted: 'text-slate-500 dark:text-slate-400',
} as const

/**
 * A single task line. The checkbox toggles DONE directly, because marking work
 * finished is by far the most frequent action and should not need a dialog.
 */
export function TaskRow({
  task,
  onOpen,
  showProject = true,
}: {
  task: Task
  onOpen: (taskId: string) => void
  showProject?: boolean
}) {
  const updateTask = useUpdateTask()
  const done = task.status === 'DONE'
  const due = describeDueDate(task.dueDate)

  function toggleDone(event: React.MouseEvent) {
    event.stopPropagation()
    const nextStatus: TaskStatus = done ? 'TODO' : 'DONE'
    updateTask.mutate({ id: task.id, status: nextStatus })
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(task.id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onOpen(task.id)
        }
      }}
      className="group flex cursor-pointer items-start gap-3 border-b border-slate-100 px-3 py-2.5 last:border-0 hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-slate-800/50"
    >
      <button
        type="button"
        onClick={toggleDone}
        disabled={updateTask.isPending}
        aria-label={done ? `Reopen ${task.title}` : `Mark ${task.title} done`}
        className={cx(
          'mt-0.5 grid h-4.5 w-4.5 shrink-0 place-items-center rounded-full border transition-colors',
          done
            ? 'border-emerald-500 bg-emerald-500 text-white'
            : 'border-slate-300 hover:border-brand-500 dark:border-slate-600',
        )}
      >
        {done && (
          <svg viewBox="0 0 12 12" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M2.5 6.5 5 9l4.5-5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </button>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span
            className={cx(
              'text-sm font-medium',
              done ? 'text-slate-400 line-through dark:text-slate-500' : '',
            )}
          >
            {task.title}
          </span>
          <PriorityFlag priority={task.priority} />
        </div>

        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
          {/* Company → project context, so a task is never ambiguous for
              someone who works across several entities. */}
          {task.company && <Chip title="Company">{task.company.shortName ?? task.company.name}</Chip>}
          {showProject && task.project && <span className="truncate">{task.project.name}</span>}
          {!done && task.dueDate && <span className={DONE_TONE[due.tone]}>{due.text}</span>}
          {task.commentCount > 0 && (
            <span className="inline-flex items-center gap-0.5">
              <svg viewBox="0 0 16 16" fill="currentColor" className="h-3 w-3" aria-hidden="true">
                <path d="M2 4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2H7l-3.3 2.5A.5.5 0 0 1 3 13v-2H4a2 2 0 0 1-2-2V4Z" />
              </svg>
              {task.commentCount}
            </span>
          )}
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {task.assignee ? (
          <Avatar name={task.assignee.name} />
        ) : (
          <span className="text-xs text-slate-400 dark:text-slate-500">Unassigned</span>
        )}
        <StatusPill status={task.status} />
      </div>
    </div>
  )
}

export function Avatar({ name, size = 'sm' }: { name: string; size?: 'sm' | 'md' }) {
  const initials = name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('')

  return (
    <span
      title={name}
      className={cx(
        // Brand-tinted rather than grey, so people read as part of the system
        // and not as placeholder chrome.
        'grid shrink-0 place-items-center rounded-full bg-brand-100 font-medium text-brand-800 dark:bg-brand-800 dark:text-brand-100',
        size === 'sm' ? 'h-6 w-6 text-[10px]' : 'h-8 w-8 text-xs',
      )}
    >
      {initials || '?'}
    </span>
  )
}

/** A titled group of task rows, used by My Work and the project view. */
export function TaskGroup({
  title,
  count,
  tone = 'default',
  tasks,
  onOpen,
  showProject = true,
}: {
  title: string
  count?: number
  tone?: 'default' | 'danger' | 'warn'
  tasks: Task[]
  onOpen: (taskId: string) => void
  showProject?: boolean
}) {
  if (tasks.length === 0) return null

  const toneClass = {
    default: 'text-slate-700 dark:text-slate-300',
    danger: 'text-red-700 dark:text-red-400',
    warn: 'text-amber-700 dark:text-amber-400',
  }[tone]

  return (
    <section className="mb-5">
      <h2 className={cx('mb-2 flex items-center gap-2 px-1 text-sm font-semibold', toneClass)}>
        {title}
        <span className="rounded-full bg-slate-100 px-1.5 text-xs font-medium text-slate-600 tabular-nums dark:bg-slate-800 dark:text-slate-400">
          {count ?? tasks.length}
        </span>
      </h2>
      <div className="card overflow-hidden">
        {tasks.map((task) => (
          <TaskRow key={task.id} task={task} onOpen={onOpen} showProject={showProject} />
        ))}
      </div>
    </section>
  )
}
